/**
 * src/components/public/forms/iletisim-form-fields.tsx
 *
 * IletisimFormu icin field sub-component'leri — RHF register prop'larini alir,
 * label/input/hata mesaji ucgenini compose eder. Hicbiri kendi state'i tutmaz;
 * sadece prop'lara react eder. Form component'inin satir sayisini code-style.md
 * 300-line hard limit'i altinda tutmak icin extracted.
 *
 * Hepsi 'use client' degil — parent ('IletisimFormu') zaten 'use client'
 * boundary'sini acti, bu dosya client component agacinda olusur, declare etmesi
 * gereksiz (next.js best practice: yalniz boundary dosyasi 'use client' alir).
 */

import type { ReactNode } from 'react'
import type { FieldError, UseFormRegisterReturn } from 'react-hook-form'

/**
 * Ortak Tailwind class'lari. inputClass + hatali state'te inputErrorClass eklenir.
 */
export const INPUT_CLASS =
  'w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors'
export const INPUT_ERROR_CLASS =
  'border-red-500 dark:border-red-400 focus:ring-red-500 focus:border-red-500'
export const LABEL_CLASS =
  'block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5'
export const ERROR_CLASS = 'mt-1 text-xs text-red-600 dark:text-red-400'

interface FieldShellProps {
  id: string
  label: string
  required?: boolean | undefined
  error?: FieldError | undefined
  errorText?: string | undefined
  children: ReactNode
}

/**
 * FieldShell — label + input wrapper + error message. Input/textarea/select
 * cocuk olarak iceriye gelir. Hata mesaji role="alert" aria-live="polite" ile
 * ekran okuyucuya bildirilir.
 *
 * @param id Input id (label.for ve aria-describedby icin)
 * @param label Gosterilen field etiketi
 * @param required true ise label'da '*' isareti
 * @param error RHF FieldError objesi
 * @param errorText Cevirilmis hata mesaji
 * @param children Input / textarea / select element'i
 */
export function FieldShell({
  id,
  label,
  required,
  error,
  errorText,
  children,
}: FieldShellProps) {
  return (
    <div>
      <label htmlFor={id} className={LABEL_CLASS}>
        {label}
        {required && (
          <>
            {' '}
            <span aria-hidden="true" className="text-red-500">
              *
            </span>
          </>
        )}
      </label>
      {children}
      {error && (
        <p
          id={`${id}-error`}
          role="alert"
          aria-live="polite"
          className={ERROR_CLASS}
        >
          {errorText ?? error.message ?? ''}
        </p>
      )}
    </div>
  )
}

interface TextFieldProps {
  id: string
  label: string
  type?: 'text' | 'email' | 'tel' | undefined
  autoComplete?: string | undefined
  required?: boolean | undefined
  placeholder?: string | undefined
  error?: FieldError | undefined
  errorText?: string | undefined
  register: UseFormRegisterReturn
}

/**
 * TextField — standart text/email/tel input + FieldShell sarmali.
 *
 * @param id HTML id
 * @param label Field label
 * @param type Input type (text default)
 * @param autoComplete HTML autocomplete hint
 * @param required Required mi
 * @param placeholder Placeholder text
 * @param error RHF error
 * @param errorText Cevirilmis hata mesaji
 * @param register RHF register('field') sonucu
 */
export function TextField({
  id,
  label,
  type = 'text',
  autoComplete,
  required,
  placeholder,
  error,
  errorText,
  register,
}: TextFieldProps) {
  return (
    <FieldShell
      id={id}
      label={label}
      required={required}
      error={error}
      errorText={errorText}
    >
      <input
        id={id}
        type={type}
        autoComplete={autoComplete}
        required={required}
        aria-required={required}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-error` : undefined}
        placeholder={placeholder}
        className={`${INPUT_CLASS} ${error ? INPUT_ERROR_CLASS : ''}`}
        {...register}
      />
    </FieldShell>
  )
}

interface TextAreaFieldProps {
  id: string
  label: string
  rows?: number | undefined
  required?: boolean | undefined
  placeholder?: string | undefined
  error?: FieldError | undefined
  errorText?: string | undefined
  register: UseFormRegisterReturn
}

/**
 * TextAreaField — cok satirli text input. min-height ile dik resize sinir
 * altinda kayma yasamadan kullanici buyutebilir.
 */
export function TextAreaField({
  id,
  label,
  rows = 6,
  required,
  placeholder,
  error,
  errorText,
  register,
}: TextAreaFieldProps) {
  return (
    <FieldShell
      id={id}
      label={label}
      required={required}
      error={error}
      errorText={errorText}
    >
      <textarea
        id={id}
        rows={rows}
        required={required}
        aria-required={required}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-error` : undefined}
        placeholder={placeholder}
        className={`${INPUT_CLASS} ${error ? INPUT_ERROR_CLASS : ''} resize-y min-h-[140px]`}
        {...register}
      />
    </FieldShell>
  )
}
